import { motion } from 'framer-motion';
import { UserSettings } from '@/types';
import { ChipGroup, ChipOption } from './ChipGroup';
import { sectionVariants } from './settings.animations';
import styles from '../Settings.module.css';

export interface GeneralSectionProps {
  theme: UserSettings['theme'];
  defaultBlockDuration: number;
  defaultStartHour: number;
  themeOptions: ChipOption<'light' | 'dark'>[];
  durationOptions: ChipOption<number>[];
  startHourOptions: ChipOption<number>[];
  onThemeChange: (value: UserSettings['theme']) => void;
  onDurationChange: (value: number) => void;
  onStartHourChange: (value: number) => void;
}

export function GeneralSection({
  theme,
  defaultBlockDuration,
  defaultStartHour,
  themeOptions,
  durationOptions,
  startHourOptions,
  onThemeChange,
  onDurationChange,
  onStartHourChange,
}: GeneralSectionProps) {
  return (
    <motion.section
      className={styles.section}
      variants={sectionVariants}
      aria-labelledby="general-heading"
    >
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon} aria-hidden="true">
          *
        </span>
        <h2 id="general-heading" className={styles.sectionTitle}>
          General
        </h2>
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Theme</p>
          <p className={styles.settingDesc}>Choose between light and dark appearance</p>
        </div>
        <ChipGroup
          options={themeOptions}
          value={theme}
          onChange={onThemeChange}
          label="Theme selection"
        />
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Default Block Duration</p>
          <p className={styles.settingDesc}>Pre-selected duration when creating new blocks</p>
        </div>
        <ChipGroup
          options={durationOptions}
          value={defaultBlockDuration}
          onChange={onDurationChange}
          label="Default block duration"
        />
      </div>

      <div className={styles.settingRow}>
        <div className={styles.settingInfo}>
          <p className={styles.settingLabel}>Timeline Start Hour</p>
          <p className={styles.settingDesc}>First hour visible on the daily timeline</p>
        </div>
        <select
          className={styles.select}
          value={defaultStartHour}
          onChange={(e) => onStartHourChange(parseInt(e.target.value, 10))}
          aria-label="Timeline start hour"
        >
          {startHourOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </motion.section>
  );
}
